import { useEffect } from 'react';
import confetti from 'canvas-confetti';
import {
    Dialog,
    DialogBackdrop,
    DialogPanel,
    DialogTitle,
} from '@headlessui/react';
import { Button } from '../buttons/Button';
import { cn } from '../../lib/helpers';

interface WeekCompleteCelebrationProps {
    title: string;
    message: string;
    intensity: 'week' | 'plan';
    onClose: () => void;
}

// Konec plánu si zaslouží hustší dávku než konec týdne.
const BURSTS: Record<'week' | 'plan', { count: number; particles: number }> = {
    week: { count: 2, particles: 70 },
    plan: { count: 4, particles: 120 },
};

export function WeekCompleteCelebration({
    title,
    message,
    intensity,
    onClose,
}: WeekCompleteCelebrationProps) {
    useEffect(() => {
        const reducedMotion = window.matchMedia(
            '(prefers-reduced-motion: reduce)'
        ).matches;

        if (reducedMotion) return;

        const { count, particles } = BURSTS[intensity];
        const timers: number[] = [];

        for (let i = 0; i < count; i++) {
            timers.push(
                window.setTimeout(() => {
                    confetti({
                        particleCount: particles,
                        spread: 80,
                        startVelocity: 45,
                        origin: { x: 0.5, y: 0.6 },
                        disableForReducedMotion: true,
                    });
                }, i * 350)
            );
        }

        return () => {
            timers.forEach((timer) => window.clearTimeout(timer));
            confetti.reset();
        };
    }, [intensity]);

    return (
        // Komponenta se v PlanWeek renderuje jen dokud existuje `celebration`,
        // takže je vždy otevřená — zavření ji z DOM odstraní přes `onClose`.
        <Dialog open onClose={onClose} className="relative z-50">
            <DialogBackdrop
                transition
                className={cn(
                    'fixed inset-0 bg-black/60',
                    'transition-opacity duration-200 data-[closed]:opacity-0'
                )}
            />

            <div className="fixed inset-0 w-screen overflow-y-auto p-4">
                <div className="flex min-h-full w-full items-center justify-center">
                    <DialogPanel
                        transition
                        className={cn(
                            'w-full max-w-dialog-md rounded-2xl bg-base-100 p-8 text-center shadow-lg',
                            'transition-all duration-200 data-[closed]:translate-y-4 data-[closed]:scale-95 data-[closed]:opacity-0'
                        )}
                    >
                        <DialogTitle className="mb-2" as="h3">
                            {title}
                        </DialogTitle>
                        <p className="mb-6 text-ink-800">{message}</p>
                        {/* Button je blokový flex box, takže ho `text-center`
                            panelu nevystředí — musí se centrovat obalem. */}
                        <div className="flex justify-center">
                            <Button color="product" onClick={onClose}>
                                Pokračovat
                            </Button>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
